import type {
  PlatformId,
  PredictionRequest,
} from "@/features/prediction/model/types";
import type { PredictionFormValues } from "@/features/prediction/model/form";
import { PLATFORM_IDS } from "@/features/prediction/model/types";

type VisualStyle = "" | "bright" | "dark" | "minimal" | "flashy";
type MusicMood = "" | "upbeat" | "calm" | "asmr" | "dramatic";
type Sentiment = "viral" | "positive" | "neutral" | "negative" | "crisis";

export interface V2PredictionStreamRequest {
  history_context: {
    request: PredictionRequest;
    review: PredictionFormValues | null;
  };
  output_language: "zh" | "en";
  caption: string;
  project_name: string;
  campaign_objective: string;
  own_brand: string;
  category: string;
  core_selling_points: string[];
  competitors: string[];
  source: "llm" | "mock" | null;
  duration_sec: number;
  visual_style: VisualStyle;
  music_mood: MusicMood;
  has_celeb: boolean;
  platform_budgets: Record<PlatformId, number>;
  audience_age_bucket: number[] | null;
  audience_gender: number[] | null;
  audience_city_tier: number[] | null;
  lifecycle_days: number;
  use_llm: boolean;
  llm_calibrate: boolean;
  n_souls: number;
  enable_crossplat: boolean;
  enable_discourse: boolean;
  discourse_n_comments: number;
  enable_groupchat: boolean;
  groupchat_n_agents: number;
  groupchat_n_rounds: number;
  enable_brand_memory: boolean;
  brand_memory_days: number;
  enable_recsys_rl: boolean;
  enable_kol_ilp: boolean;
  enable_search_elasticity: boolean;
  today: string | null;
  daypart: "auto" | "morning" | "noon" | "afternoon" | "evening" | "late";
  weather_temp_c: number;
  weather_desc: string | null;
  rainy: boolean;
  season: string | null;
  holiday_context: string | null;
  upcoming_holiday: string | null;
  sentiment: Sentiment;
  sentiment_reason: string | null;
  world_news_summary: string[] | null;
  competitor_moves: string[] | null;
  social_trends: string[] | null;
  policy_regulation: string[] | null;
  cross_platform_overlap: number;
}

function enumValue<T extends string>(
  value: string,
  choices: readonly T[],
  fallback: T,
): T {
  return choices.find((choice) => choice === value) ?? fallback;
}

function genderArray(
  value: PredictionRequest["audience_gender"],
): number[] | null {
  if (Array.isArray(value)) return value.length ? value : null;
  return value === null || value === undefined ? null : [value];
}

function platformBudgets(
  request: PredictionRequest,
): Record<PlatformId, number> {
  const budgets: Record<PlatformId, number> = {
    douyin: 0,
    xhs: 0,
    wechat_video: 0,
    bilibili: 0,
    kuaishou: 0,
    tiktok: 0,
    instagram: 0,
    youtube: 0,
  };
  for (const platform of PLATFORM_IDS) {
    budgets[platform] = Math.max(
      0,
      Math.round(
        request.platform_budgets?.[platform] ??
          request.total_budget * (request.platform_alloc[platform] || 0),
      ),
    );
  }
  return budgets;
}

export function toV2PredictionRequest(
  request: PredictionRequest,
  review: PredictionFormValues | null = null,
  outputLanguage: "zh" | "en" = "en",
): V2PredictionStreamRequest {
  return {
    history_context: { request, review },
    output_language: outputLanguage,
    caption: request.creative.caption,
    project_name: request.project_name || "",
    campaign_objective: request.campaign_objective || "",
    own_brand: request.own_brand || "",
    category: request.category || "",
    core_selling_points: request.core_selling_points || [],
    competitors: request.competitors || [],
    source: request.source || null,
    duration_sec: request.creative.duration_sec,
    visual_style: enumValue(
      request.creative.visual_style,
      ["", "bright", "dark", "minimal", "flashy"] as const,
      "bright",
    ),
    music_mood: enumValue(
      request.creative.music_mood,
      ["", "upbeat", "calm", "asmr", "dramatic"] as const,
      "upbeat",
    ),
    has_celeb: request.creative.has_celeb,
    platform_budgets: platformBudgets(request),
    audience_age_bucket: request.audience_age_buckets || null,
    audience_gender: genderArray(request.audience_gender),
    audience_city_tier: request.audience_city_tiers || null,
    lifecycle_days: request.lifecycle_days,
    use_llm: request.use_llm,
    llm_calibrate: request.llm_calibrate,
    n_souls: request.n_souls,
    enable_crossplat: request.enable_crossplat,
    enable_discourse: request.enable_discourse,
    discourse_n_comments: request.discourse_n_comments,
    enable_groupchat: request.enable_groupchat,
    groupchat_n_agents: request.groupchat_n_agents,
    groupchat_n_rounds: request.groupchat_n_rounds,
    enable_brand_memory: request.enable_brand_memory,
    brand_memory_days: request.brand_memory_days || 90,
    enable_recsys_rl: request.enable_recsys_rl,
    enable_kol_ilp: request.enable_kol_ilp,
    enable_search_elasticity: request.enable_search_elasticity,
    today: request.today || null,
    daypart: enumValue(
      request.daypart,
      ["auto", "morning", "noon", "afternoon", "evening", "late"] as const,
      "auto",
    ),
    weather_temp_c: request.weather_temp_c,
    weather_desc: request.weather_desc || null,
    rainy: request.rainy,
    season: request.season || null,
    holiday_context: request.holiday_context || null,
    upcoming_holiday: request.upcoming_holiday || null,
    sentiment: enumValue(
      request.sentiment,
      ["viral", "positive", "neutral", "negative", "crisis"] as const,
      "neutral",
    ),
    sentiment_reason: request.sentiment_reason || null,
    world_news_summary: request.world_news_summary || null,
    competitor_moves: request.competitor_moves || null,
    social_trends: request.social_trends || null,
    policy_regulation: request.policy_regulation || null,
    cross_platform_overlap: request.cross_platform_overlap,
  };
}
